// ANX-053 — звук + browser-notification оператору при сообщении visitor'а.
//
// Per-device preferences хранятся в localStorage (sync между устройствами
// не делаем — оператор на телефоне может хотеть тишину, на десктопе — звук).
// Default — оба канала включены.

const KEY_SOUND = 'anx_notify_sound';
const KEY_BROWSER = 'anx_notify_browser';

export interface Prefs {
  sound: boolean;
  browser: boolean;
}

export function getPrefs(): Prefs {
  if (typeof localStorage === 'undefined') return { sound: true, browser: true };
  return {
    sound: localStorage.getItem(KEY_SOUND) !== 'off',
    browser: localStorage.getItem(KEY_BROWSER) !== 'off',
  };
}

export function setPrefs(p: Partial<Prefs>): void {
  if (typeof localStorage === 'undefined') return;
  if (p.sound !== undefined) localStorage.setItem(KEY_SOUND, p.sound ? 'on' : 'off');
  if (p.browser !== undefined) localStorage.setItem(KEY_BROWSER, p.browser ? 'on' : 'off');
}

let audioCtx: AudioContext | null = null;

/** Только для тестов — сбросить кешированный AudioContext. */
export function _resetAudioCtxForTests(): void {
  audioCtx = null;
}

function getAudioCtx(): AudioContext | null {
  if (audioCtx) return audioCtx;
  if (typeof window === 'undefined') return null;
  const Ctx =
    (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ??
    (globalThis as unknown as { AudioContext?: typeof AudioContext }).AudioContext;
  if (!Ctx) return null;
  try {
    audioCtx = new Ctx();
    return audioCtx;
  } catch {
    return null;
  }
}

export function playBeep(): void {
  if (!getPrefs().sound) return;
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') void ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.value = 0.3;
    osc.connect(gain).connect(ctx.destination);
    const now = ctx.currentTime;
    osc.start(now);
    osc.stop(now + 0.18);
  } catch {
    // На некоторых платформах AudioContext может бросить если user-activation истёк.
    // Не критично — звук просто не сыграет.
  }
}

export function showBrowserNotification(title: string, body: string): void {
  if (!getPrefs().browser) return;
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  try {
    new Notification(title, { body, icon: '/favicon.svg' });
  } catch {
    // Некоторые браузеры бросают на insecure-context'е или iframe — молчим.
  }
}

export async function requestBrowserPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied';
  if (Notification.permission !== 'default') return Notification.permission;
  return await Notification.requestPermission();
}

export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}
